import React, { useEffect } from "react";
import DefaultLayout from "../../layout/DefaultLayout";
import SummaryCard from "../../components/Cards/SummaryCard";
import DonutChart from "../../components/Charts/DonutChart";
import ChartDynamic from "../../components/Charts/ChartDynamic";
import useSaleDashboard from "../../hooks/dashboard/useSaleDashboard";

const SalesDashboard: React.FC = () => {
  const {
    stats,
    monthlyStats,
    dailyStats,
    fetchStats,
    fetchMonthlySales,
    fetchDailySales,
  } = useSaleDashboard();

  // ==========================================================
  // 🔄 Cargar datos iniciales al montar
  // ==========================================================
  useEffect(() => {
    const year = new Date().getFullYear();
    const month = new Date().getMonth() + 1;

    fetchStats();
    fetchMonthlySales(year);
    fetchDailySales(year, month);
  }, []);

  // ==========================================================
  // 🎨 Configuración de colores y datos
  // ==========================================================
  const statusLabels = ["Pendiente", "Finalizada", "Anulada"];
  const statusSeries = [
    stats.salesByStatus["Pendiente"] || 0,
    stats.salesByStatus["Finalizada"] || 0,
    stats.salesByStatus["Anulada"] || 0,
  ];

  const docTypeLabels = Object.keys(stats.salesByDocumentType || {});
  const branchLabels = Object.keys(stats.salesByBranch || {});
  const userLabels = Object.keys(stats.salesByUser || {});

  const docTypeColors = ["#8FD0EF", "#0FADCF", "#6366F1", "#FBBF24"];
  const branchColors = ["#F97316", "#14B8A6", "#6366F1", "#F59E0B"];
  const userColors = ["#0EA5E9", "#EC4899", "#10B981", "#F97316"];
  const statusColors = ["#3C50E0", "#16A34A", "#EF4444"];

  // ==========================================================
  // 🧩 Render principal
  // ==========================================================
  return (
    <DefaultLayout>
      {/* ======================================================
         🎯 Tarjetas resumen
      ====================================================== */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4 2xl:gap-7.5">
        <SummaryCard
          title="Total de Ventas"
          value={stats.totalSales.toString()}
          iconName="shopping-bag"
        />
        <SummaryCard
          title="Pendientes"
          value={(stats.salesByStatus["Pendiente"] || 0).toString()}
          iconName="clock"
        />
        <SummaryCard
          title="Finalizadas"
          value={(stats.salesByStatus["Finalizada"] || 0).toString()}
          iconName="check-circle"
        />
        <SummaryCard
          title="Anuladas"
          value={(stats.salesByStatus["Anulada"] || 0).toString()}
          iconName="x-circle"
        />
      </div>

      {/* ======================================================
         📊 Gráfico de ventas mensuales
      ====================================================== */}
      <div className="mt-8 grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <ChartDynamic
            title="Ventas mensuales"
            type="monthly"
            enableFilters
            data={monthlyStats.values}
            categories={monthlyStats.labels}
            onFilterChange={(year) => fetchMonthlySales(year)}
          />
        </div>
      </div>

      {/* ======================================================
         📆 Gráfico de ventas diarias (por mes)
      ====================================================== */}
      <div className="mt-8 grid grid-cols-12 gap-4">
        <div className="col-span-12">
          <ChartDynamic
            title="Ventas diarias"
            type="daily"
            enableFilters
            data={dailyStats.values}
            categories={dailyStats.labels}
            onFilterChange={(year, month) => fetchDailySales(year, month)}
          />
        </div>
      </div>

      {/* ======================================================
         🧁 Gráficos Donut (comparativos)
      ====================================================== */}
      <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
        <DonutChart
          title="Por tipo de documento"
          labels={docTypeLabels}
          series={Object.values(stats.salesByDocumentType || {})}
          colors={docTypeColors.slice(0, docTypeLabels.length)}
        />

        <DonutChart
          title="Por sucursal"
          labels={branchLabels}
          series={Object.values(stats.salesByBranch || {})}
          colors={branchColors.slice(0, branchLabels.length)}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
        <DonutChart
          title="Por usuario"
          labels={userLabels}
          series={Object.values(stats.salesByUser || {})}
          colors={userColors.slice(0, userLabels.length)}
        />

        <DonutChart
          title="Por estado"
          labels={statusLabels}
          series={statusSeries}
          colors={statusColors.slice(0, statusLabels.length)}
        />
      </div>
    </DefaultLayout>
  );
};

export default SalesDashboard;
